import fallbackRestaurants from "./fallbackRestaurants.json";

/* ---------------------------------------------------------------
   TEMPORARY — offline copy of GET /api/v1/restaurants.
   Remove together with the fallback in restaurantApi.js once the
   backend is deployed.

   fallbackRestaurants.json is a copy of the backend's seed file
   (server/src/database/seed/restaurants.json), and the functions
   below port the backend's in-memory repository and service
   (memoryRestaurantRepository.ts, restaurantSort.ts,
   restaurant.service.ts), so the page gets the same filtering,
   ordering, facets and response shape the API produces.

   Only the cursor differs: it is a plain offset, prefixed "fb:" so it
   is never mistaken for (or sent as) a real API cursor.
----------------------------------------------------------------*/

const SORT_LAST_ASC = 2_147_483_647;
const SORT_LAST_DESC = -1;

const SORT_SPECS = {
  relevance: { field: "featuredPosition", direction: "asc" },
  rating: { field: "avgRating", direction: "desc" },
  delivery_time: { field: "deliveryMinutes", direction: "asc" },
  cost_asc: { field: "costForTwoPaise", direction: "asc" },
  cost_desc: { field: "costForTwoPaise", direction: "desc" },
};

export const FALLBACK_CURSOR_PREFIX = "fb:";

const lower = (value) => value.toLowerCase();

function sortValueOf(restaurant, sort) {
  const { field, direction } = SORT_SPECS[sort];
  return restaurant[field] ?? (direction === "asc" ? SORT_LAST_ASC : SORT_LAST_DESC);
}

function compareRestaurants(sort) {
  const { direction } = SORT_SPECS[sort];
  return (a, b) => {
    const difference = sortValueOf(a, sort) - sortValueOf(b, sort);
    if (difference !== 0) return direction === "asc" ? difference : -difference;
    return Number(a.id) - Number(b.id);
  };
}

function matches(restaurant, filters, { ignoreCuisine = false } = {}) {
  const { q, city, cuisine, pureVeg, minRating, maxDeliveryMinutes } = filters;

  if (q) {
    const needle = lower(q);
    const haystack = [
      restaurant.name,
      restaurant.areaName,
      restaurant.locality ?? "",
      ...restaurant.cuisines,
    ];
    if (!haystack.some((text) => lower(text).includes(needle))) return false;
  }
  if (city && lower(restaurant.city) !== lower(city)) return false;
  if (!ignoreCuisine && cuisine && !restaurant.cuisines.includes(cuisine)) return false;
  if (pureVeg !== undefined && restaurant.isPureVeg !== pureVeg) return false;
  if (minRating !== undefined && (restaurant.avgRating ?? -1) < minRating) return false;
  if (
    maxDeliveryMinutes !== undefined &&
    (restaurant.deliveryMinutes ?? Infinity) > maxDeliveryMinutes
  ) {
    return false;
  }
  const cost = restaurant.costForTwoPaise;
  if (filters.minCostForTwoPaise !== undefined && (cost ?? -1) < filters.minCostForTwoPaise) {
    return false;
  }
  if (
    filters.maxCostForTwoPaise !== undefined &&
    (cost ?? Infinity) > filters.maxCostForTwoPaise
  ) {
    return false;
  }
  return true;
}

function cuisineFacets(restaurants) {
  const counts = new Map();
  restaurants.forEach((restaurant) =>
    new Set(restaurant.cuisines).forEach((cuisine) =>
      counts.set(cuisine, (counts.get(cuisine) ?? 0) + 1),
    ),
  );
  return [...counts]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
}

const toCard = ({ featuredPosition, ...card }) => card;

/** Same response `data` as GET /api/v1/restaurants, from the bundled copy. */
export function buildFallbackRestaurantPage({
  sort = "relevance",
  limit = 20,
  cursor,
  ...filters
}) {
  const offset = cursor?.startsWith(FALLBACK_CURSOR_PREFIX)
    ? Number(cursor.slice(FALLBACK_CURSOR_PREFIX.length)) || 0
    : 0;

  const matching = fallbackRestaurants.restaurants
    .filter((restaurant) => matches(restaurant, filters))
    .sort(compareRestaurants(sort));
  const page = matching.slice(offset, offset + limit);
  const nextOffset = offset + page.length;

  return {
    restaurants: page.map(toCard),
    total: matching.length,
    nextCursor:
      nextOffset < matching.length ? `${FALLBACK_CURSOR_PREFIX}${nextOffset}` : null,
    facets: {
      cuisines: cuisineFacets(
        fallbackRestaurants.restaurants.filter((restaurant) =>
          matches(restaurant, filters, { ignoreCuisine: true }),
        ),
      ),
    },
    isFallback: true,
  };
}

/** Header details for the restaurant page, until GET /restaurants/:id exists. */
export const findFallbackRestaurant = (id) =>
  fallbackRestaurants.restaurants.find((restaurant) => restaurant.id === String(id)) ?? null;
